// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, LinearProgress } from '@mui/material';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import FormDialog from '../../../components/Dialog/FormDialog';
import useArchiveAndCreateProviderConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveAndCreateProviderConfiguration';
import useProviderService from '../../../hooks/queries/registry/onboarding/useProviderService';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
  fromPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import TemplateContract from '../../../utils/TemplateContract';
import { ERR_MOD_PROVIDER_CONF } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  providerConfiguration: TemplateContract<ProviderConfiguration>;
};

export const ModifyProviderConfigurationDialog = ({
  open,
  onClose,
  providerConfiguration,
}: DialogProps) => {
  const [registrarCredentialRequirements, setRegistrarCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);

  useEffect(() => {
    if (providerConfiguration?.payload?.registrarRequirements) {
      setRegistrarCredentialRequirements(
        fromPartyCredentialRequirements(providerConfiguration.payload.registrarRequirements),
      );
    }
  }, [providerConfiguration]);

  const providerService = useProviderService();
  const archiveAndCreateProviderConfiguration = useArchiveAndCreateProviderConfiguration();
  const { showBoundary } = useErrorBoundary<Error>();

  const resetState = useCallback(() => {
    setRegistrarCredentialRequirements([]);
  }, [setRegistrarCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!providerService.data) {
      showBoundary(
        new Error(ERR_MOD_PROVIDER_CONF, { cause: 'Provider service is not available.' }),
      );
      return;
    }
    await archiveAndCreateProviderConfiguration.submit({
      cid: providerService.data.contractId,
      argument: {
        cid: providerConfiguration.contractId,
        payload: {
          holderRequirements: [], // Disabled as of version `0.7.0` of the utility-registry-app-v0 package
          registrarRequirements: toPartyCredentialRequirements(registrarCredentialRequirements),
        },
      },
    });
    handleClose();
  }, [
    registrarCredentialRequirements,
    archiveAndCreateProviderConfiguration,
    providerConfiguration.contractId,
    providerService.data,
    handleClose,
    showBoundary,
  ]);

  if (providerService.isLoading) {
    return <LinearProgress />;
  }

  const disableSubmit = archiveAndCreateProviderConfiguration.status === 'pending';
  return (
    <FormDialog
      open={open}
      title="Modify Provider Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Modify"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={registrarCredentialRequirements}
          setList={setRegistrarCredentialRequirements}
          title="Required Credentials for Registrar"
        />
      </Box>
    </FormDialog>
  );
};
